import React from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { QuestionFilterPanel } from "./QuestionFilterPanel";
import { ExampleQuestionsCarousel } from "components/shared/ExampleQuestionsCarousel/ExampleQuestionsCarousel";
import { useQuestionFacets, useStandardQuestionFacets } from "hooks/useQuestionFacets/useQuestionFacets";
import { useLessonQuestions, useStandardScopedQuestions } from "hooks/useCurriculum/useCurriculum";

// Always-on filter panel + the filtered question carousel, for one lesson OR standard
// scope. The single reusable composition of the built primitives (QuestionFilterPanel,
// ExampleQuestionsCarousel, and the lesson-/standard-scoped facets+questions hooks) —
// used inline in the Advanced Selection surfaces so the filter+carousel isn't
// re-authored per screen.
//
// Scope — exactly one of:
//   - Lesson (pass lessonId): Core (multi-standard, pass showStandards → the Standards
//     dropdown narrows the lesson's addressing set) or single standard (prereq/
//     extension, also pass standardCode to narrow to that one sub-item).
//   - Standard flow (pass standardCode, omit lessonId): the standard flow's own Core /
//     prerequisite / extension standards — one
//     leaf standard is the whole scope, so showStandards has no meaning here.
//
// Props:
//   lessonId?, standardCode?, filter (controlled), onFilterChange,
//   showStandards? (lesson Core only), showAutoAssign?
export const QuestionFilterView = ({
  lessonId,
  standardCode = null,
  filter,
  onFilterChange,
  showStandards = false,
  showAutoAssign = false,
  // Optional carousel background so it blends into the host page.
  background,
}) => {
  const isStandardScope = !lessonId && !!standardCode;

  const query = {
    standardCode: !isStandardScope ? (standardCode || undefined) : undefined,
    standards: showStandards ? filter.standards : undefined,
    learningComponents: filter.learningComponents,
    questionType: filter.questionType,
  };

  const { facets: lessonFacets, hasLoaded: lessonLoaded, error: lessonError } = useQuestionFacets(
    !isStandardScope ? lessonId : null,
    { standardCode: standardCode || null, ...query },
  );
  const {
    facets: standardFacets,
    hasLoaded: standardLoaded,
    error: standardError,
  } = useStandardQuestionFacets(isStandardScope ? standardCode : null, query);
  const facets = isStandardScope ? standardFacets : lessonFacets;
  // Same scope pick as `facets` above. Distinct from the `loading` below, which belongs to
  // the questions carousel, not the facets.
  const facetsLoaded = isStandardScope ? standardLoaded : lessonLoaded;
  const facetsError = isStandardScope ? standardError : lessonError;

  const lessonQuestions = useLessonQuestions(!isStandardScope ? lessonId : null, query);
  const standardQuestions = useStandardScopedQuestions(isStandardScope ? standardCode : null, query);
  const { questions, total, loading, loadMore } = isStandardScope ? standardQuestions : lessonQuestions;

  return (
    <>
      <QuestionFilterPanel
        filter={filter}
        onChange={onFilterChange}
        facets={facets}
        hasLoaded={facetsLoaded}
        error={facetsError}
        showStandards={showStandards}
        showAutoAssign={showAutoAssign}
      />
      {loading ? (
        <Loading>
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        </Loading>
      ) : questions?.length > 0 ? (
        <ExampleQuestionsCarousel questions={questions} total={total} onLoadMore={loadMore} background={background} />
      ) : (
        <Empty>No questions available for this selection.</Empty>
      )}
    </>
  );
};

const Loading = styled.div`
  text-align: center;
  padding: 32px;
`;

const Empty = styled.p`
  color: #6b6967;
  font-size: 16px;
  padding: 16px 0;
`;
